"use client";

import type { ReactNode } from "react";
import React from "react";

type Props = {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackClassName?: string;
};

type State = {
  hasError: boolean;
  message?: string;
  stack?: string;
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    const err = error as { message?: string; stack?: string };
    return {
      hasError: true,
      message: typeof err?.message === "string" ? err.message : String(error),
      stack: typeof err?.stack === "string" ? err.stack : undefined,
    };
  }

  componentDidCatch(error: unknown) {
    // Keep this as a plain console.error so Vercel logs capture it.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Caught error:", error);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const wrapperClassName = this.props.fallbackClassName ?? "min-h-screen p-6";

    return (
      <div className={wrapperClassName}>
        <div className="max-w-3xl mx-auto rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="font-semibold text-red-900">
            {this.props.fallbackTitle ?? "Client error"}
          </div>
          <div className="mt-2 text-sm text-red-800 break-words">
            {this.state.message}
          </div>
          {this.state.stack ? (
            <pre className="mt-3 max-h-[50vh] overflow-auto rounded-lg bg-white/70 p-3 text-xs text-red-900 whitespace-pre-wrap">
              {this.state.stack}
            </pre>
          ) : null}
          <div className="mt-3 text-xs text-red-700">
            If you send me this error box + stack, I can fix the exact line.
          </div>
        </div>
      </div>
    );
  }
}

